<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useSmarticoMissions } from '@/stores/smartico/missions';
import { useAuthStore } from '@/stores/auth';
import type {
  AffiliateConfig,
  BannerItem,
  AffiliateDetailsConfig,
} from '~/utils/perfilOficialConfigs';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';

const { getResponsiveBannerUrl } = useResponsiveBannerUrl();

const props = defineProps<{
  banner: BannerItem[];
  affiliateDetails?: AffiliateDetailsConfig;
  config: AffiliateConfig;
  isRoletasPage: boolean;
  promocode?: string;
  accentColor?: string;
}>();

const avatarVariant = computed(
  () => props.affiliateDetails?.avatarVariant ?? 'circle',
);

const avatarBorderColor = computed(() => {
  if (props.accentColor) return props.accentColor;

  return props.isRoletasPage ? '#FFD700' : '#00C9FF';
});

const avatarGradient = computed(() => {
  if (props.accentColor) {
    return `background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, ${props.accentColor} 45.19%, ${props.accentColor}80 100%)`;
  }

  return props.isRoletasPage
    ? 'background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, #FFD700 45.19%, #A0522D 100%)'
    : 'background: radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, #00C9FF 45.19%, #0003BE 100%)';
});

const promocode = ref(props.promocode || '');
const windowWidth = ref(0);
const isLoading = ref(true);
const imagesLoaded = ref(0);
const totalImages = computed(() => props.banner.length);

const promocodeStore = usePromocodeStore();

const isPromocodeButtonActive = computed(() => {
  return promocode.value.trim().length > 0 && !promocodeStore.isRedeeming;
});

const isMobile = computed(() => {
  const mobile = windowWidth.value < 600;

  return mobile;
});

const promocodeInputMinWidthClass = computed(() => {
  return windowWidth.value < 1200 ? 'min-w-[230px]' : 'min-w-[330px]';
});

const aspectRatioPadding = computed(() => {
  return isMobile.value ? '42.23%' : '33.07%';
});

const { loadMissions } = useSmarticoMissions();

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);

// Update windowWidth on resize
function updateWindowWidth() {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  loadMissions();
  updateWindowWidth();
  window.addEventListener('resize', updateWindowWidth);
});

function handleImageLoad() {
  imagesLoaded.value++;
  if (imagesLoaded.value === totalImages.value) {
    nextTick(() => {
      isLoading.value = false;
    });
  }
}

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth);
});

async function applyPromocode() {
  const success = await promocodeStore.redeemPromocode(promocode.value);

  if (success) promocode.value = '';
}
</script>

<template>
  <div
    class="relative w-full md:rounded-lg -mt-3 mb-3.5 select-none md:border md:border-[#1D2329] md:border-solid overflow-hidden"
    :class="{ 'bg-black': props.isRoletasPage }"
  >
    <div
      class="relative"
      :style="{ paddingTop: aspectRatioPadding }"
    >
      <!-- Skeleton loader / Placeholder while loading -->
      <div
        v-if="isLoading || props.banner.length === 0"
        class="absolute inset-0 z-0 h-auto w-full rounded-t-lg bg-[#00000040] animate-pulse"
      ></div>

      <Swiper
        v-if="props.banner?.length > 0"
        :autoplay="{
          delay: 8000,
          disableOnInteraction: false,
        }"
        class="absolute inset-0 z-0 h-auto w-full rounded-t-lg"
        :modules="[Autoplay]"
        :slides-per-view="1"
        :space-between="0"
      >
        <SwiperSlide
          v-for="(bannerItem, index) in props.banner"
          :key="index"
        >
          <NuxtImg
            :alt="bannerItem.alt"
            class="h-auto w-full rounded-t-lg select-none object-fill -mt-1 md:-mt-2"
            loading="eager"
            :src="getResponsiveBannerUrl(bannerItem.image)"
            @dragstart.prevent
            @error="handleImageLoad"
            @load="handleImageLoad"
          />
        </SwiperSlide>
      </Swiper>
    </div>

    <!-- Affiliate Section - OVERLAY on banner -->
    <div
      v-if="props.affiliateDetails"
      class="absolute bottom-0 left-0 right-0 z-10 p-4 md:pt-7"
      :style="
        props.isRoletasPage
          ? 'background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 26.63%);'
          : 'background: linear-gradient(180deg, rgba(1, 6, 18, 0) 0%, #020716 26.63%);'
      "
    >
      <div
        class="w-full flex flex-col md:flex-row flex-wrap justify-between items-start md:items-center md:pt-3"
      >
        <!-- Left - Affiliate Profile -->
        <div class="flex items-center relative">
          <!-- Avatar redondo (perfis oficiais comuns) -->
          <div
            v-if="avatarVariant !== 'icon'"
            class="w-[70px] h-[70px] sm:w-24 sm:h-24 rounded-full border-2 border-solid overflow-hidden absolute -top-5 sm:-top-10 left-0"
            :style="[{ borderColor: avatarBorderColor }, avatarGradient]"
          >
            <!-- Simulating a profile picture -->
            <NuxtImg
              v-if="props.affiliateDetails.avatarUrl"
              alt="Avatar Affiliate"
              class="object-cover"
              draggable="false"
              :src="props.affiliateDetails.avatarUrl"
            />
          </div>

          <!-- Avatar como ícone livre (ex.: taça da Copa em /perfil-oficial/esportes) -->
          <div
            v-else
            class="w-[70px] h-[70px] sm:w-24 sm:h-24 absolute -top-5 sm:-top-10 left-0 flex items-center justify-center"
          >
            <NuxtImg
              v-if="props.affiliateDetails.avatarUrl"
              alt="Avatar Affiliate"
              class="w-full h-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.6)]"
              draggable="false"
              :src="props.affiliateDetails.avatarUrl"
            />
          </div>

          <div class="ml-[82px] sm:ml-28">
            <div class="flex items-center gap-1.5 space-x-1 mb-0.5">
              <h1
                class="text-white text-center text-sm md:text-lg font-bold leading-7"
              >
                {{ props.affiliateDetails.name }}
              </h1>
              <NuxtIcon
                v-if="props.affiliateDetails.iconName"
                class="text-white text-[20px]"
                :filled="true"
                :name="props.affiliateDetails.iconName"
              />
            </div>

            <p
              class="self-stretch text-[rgba(255,255,255,0.50)] text-xs md:text-sm font-medium leading-[18px]"
            >
              {{ props.affiliateDetails.title }}
            </p>
          </div>
        </div>

        <!-- Right - Promotional Code (Desktop) -->
        <div
          v-if="isAuthenticated && props.config.showPromocodeInput"
          class="h-[64px] shrink-0 rounded-[10px] p-[10px] w-full md:w-auto hidden lg:block border backdrop-blur-[10px] border-solid border-[#1D2329]"
        >
          <div
            class="flex items-center gap-2.5 w-full h-full [background:rgba(199,199,199,0.05)] rounded-md"
          >
            <input
              v-model="promocode"
              class="flex-grow h-[44px] rounded-md px-[20px] sm:text-[13px] text-[10px] font-normal text-[#B0BED9] overflow-hidden text-ellipsis whitespace-nowrap"
              :class="[
                promocodeInputMinWidthClass,
                props.isRoletasPage ? 'bg-[#1A1A1A]' : 'bg-[#0C1B44]',
              ]"
              maxlength="40"
              :placeholder="props.affiliateDetails?.promoCodePlaceholder"
              type="text"
              @keyup.enter="applyPromocode"
            />
            <button
              class="flex justify-center items-center gap-[7px] self-stretch px-4 py-2.5 rounded-md text-center text-sm font-extrabold leading-3"
              :class="
                isPromocodeButtonActive
                  ? '[background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984] text-white'
                  : '[background:#303747] text-[#B7B7B7]'
              "
              :disabled="!isPromocodeButtonActive"
              @click="applyPromocode"
            >
              <NuxtIcon
                v-if="promocodeStore.isRedeeming"
                class="w-5 -mx-1 animate-spin"
                name="loading"
                style="width: 12px; height: 12px"
              />
              {{ props.affiliateDetails?.redeemButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Bottom - Promotional Code mobile -->
  <div
    v-if="isAuthenticated && props.config.showPromocodeInput"
    class="h-[32px] shrink-0 -mt-[12px] mb-4 w-full block lg:hidden"
  >
    <div class="flex items-center gap-2.5 w-full h-full rounded-md">
      <input
        v-model="promocode"
        class="flex-grow h-full rounded-md px-[20px] sm:text-[13px] text-[10px] font-normal text-[#B0BED9]"
        :class="props.isRoletasPage ? 'bg-[#1A1A1A]' : 'bg-[#0C1B44]'"
        maxlength="40"
        :placeholder="props.affiliateDetails?.promoCodePlaceholder"
        type="text"
        @keyup.enter="applyPromocode"
      />
      <button
        class="flex justify-center items-center gap-[7px] self-stretch px-[30px] py-1 sm:px-4 sm:py-2.5 sm:w-[220px] rounded-md text-center text-[10px] sm:text-sm font-extrabold leading-3"
        :class="
          isPromocodeButtonActive
            ? '!text-white [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] border-[#FC9984]'
            : 'text-[#B7B7B7] [background:#303747]'
        "
        :disabled="!isPromocodeButtonActive"
        @click="applyPromocode"
      >
        <NuxtIcon
          v-if="promocodeStore.isRedeeming"
          class="w-5 -mx-1 animate-spin"
          name="loading"
          style="width: 12px; height: 12px"
        />
        {{ props.affiliateDetails?.redeemButtonText }}
      </button>
    </div>
  </div>
</template>
